import {
  Button,
  Form,
  FormGroup,
  Modal,
  ModalVariant,
  Select,
  SelectOption,
  SelectVariant,
  TextArea,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'

import { useReasonsForDiscontinuation } from '@app/domain/program/program.hooks'

interface Props {
  isModalOpen: boolean
  onCancel: () => void
  onConfirm: (reason: string, description: string) => void
}

export const DiscontinueProgramModal = function ({
  isModalOpen,
  onCancel,
  onConfirm,
}: Props) {
  const [isSelectReasonOpen, setIsSelectReasonOpen] = useState<boolean>(false)

  const [reason, setReason] = useState<string>('')
  const [reasonDescription, setReasonDescription] = useState<string>('')

  const { reasons } = useReasonsForDiscontinuation()
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(true)

  const handleConfirm = useCallback(() => {
    setReason('')
    setReasonDescription('')
    onConfirm(reason, reasonDescription)
  }, [onConfirm, reason, reasonDescription])

  const handleCancel = useCallback(() => {
    setReason('')
    setReasonDescription('')
    onCancel()
  }, [onCancel])

  const children = React.useMemo(() => {
    return reasons.map((r, index) => {
      return <SelectOption key={index} value={r} selected={reason === r} />
    })
  }, [reason, reasons])

  const onSelect = useCallback((event, selection, isPlaceholder) => {
    if (isPlaceholder) {
      setReason('')
    } else {
      setReason(selection)
    }
    setReasonDescription('')
    setIsSelectReasonOpen(false)
  }, [])

  const onToggle = useCallback(() => {
    setIsSelectReasonOpen(!isSelectReasonOpen)
  }, [isSelectReasonOpen])

  useEffect(() => {
    setIsFormDisabled(reason === '')
  }, [reason])

  return (
    <Modal
      variant={ModalVariant.medium}
      title="Discontinue Patient From Program"
      isOpen={isModalOpen}
      onClose={handleCancel}
      actions={[
        <Button
          key="confirm"
          variant="primary"
          onClick={handleConfirm}
          isDisabled={isFormDisabled}
        >
          Discontinue Patient
        </Button>,
        <Button key="cancel" variant="link" onClick={handleCancel}>
          Cancel
        </Button>,
      ]}
    >
      <Form isHorizontal>
        <FormGroup label="Reason" fieldId="reason" isRequired>
          <Select
            variant={SelectVariant.single}
            aria-label="Select Reason"
            onToggle={onToggle}
            onSelect={onSelect}
            selections={reason}
            isOpen={isSelectReasonOpen}
            placeholderText="Please Select"
            menuAppendTo="parent"
          >
            {children}
          </Select>
        </FormGroup>
        <FormGroup label="Description" fieldId="description">
          <TextArea
            id="reason_description"
            value={reasonDescription}
            onChange={setReasonDescription}
          />
        </FormGroup>
      </Form>
    </Modal>
  )
}
